import { playerGuardiansKeys } from '@/queries/use-player-guardians-query';
import { fetchInvitationPreview } from '@/services/get-player-invitations';
import { revokePlayerInvitation } from '@/services/revoke-player-invitation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const invitationPreviewKeys = {
  all: ['invitation-preview'] as const,
  detail: (token?: string) => [...invitationPreviewKeys.all, token ?? null] as const,
};

/**
 * Preview an invitation by token from
 * GET `/api/v1/invitations/{token}`. Gated on `token`.
 */
export function useInvitationPreviewQuery(token?: string) {
  return useQuery({
    queryKey: invitationPreviewKeys.detail(token),
    queryFn: () => fetchInvitationPreview(token as string),
    enabled: Boolean(token),
  });
}

/**
 * Revokes a still-pending invitation via
 * DELETE `/api/v1/guardians/me/players/{playerId}/invitations/{invitationId}`.
 *
 * The invitation is NOT deleted here — it flips to revoked/expired — so it's safe to refetch
 * its own preview (GET /invitations/{token}) to show the new status on the detail screen we
 * stay on, alongside the circle list. To remove an already-dead invitation (which IS deleted,
 * so refetching its token would 404) use useRemovePlayerInvitationMutation instead.
 */
export function useRevokePlayerInvitationMutation(playerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokePlayerInvitation(playerId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerGuardiansKeys.list(playerId) });
      queryClient.invalidateQueries({ queryKey: invitationPreviewKeys.all });
    },
  });
}

/**
 * Removes an already-dead invitation (revoked/declined/expired) via the same DELETE endpoint
 * as revoke — the server deletes it outright once the invitation is dead.
 *
 * Only invalidates the circle LIST so the row disappears. Unlike revoke, it must NOT refetch
 * the invitation preview by token: the record is gone, so GET /invitations/{token} would 404.
 * The caller navigates back to the circle right after this.
 */
export function useRemovePlayerInvitationMutation(playerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokePlayerInvitation(playerId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerGuardiansKeys.list(playerId) });
    },
  });
}
